import { b2Mat22, b2Vec2 } from "./common/math.js";
import { b2Settings } from "./common/settings.js";

export class b2AABB {
  constructor() {
    this.upperBound = new b2Vec2();
    this.lowerBound = new b2Vec2();
  }

  IsValid() {
    const dx = this.upperBound.x - this.lowerBound.x;
    const dy = this.upperBound.y - this.lowerBound.y;
    const valid = dx >= 0 && dy >= 0;
    return valid && this.lowerBound.IsValid() && this.upperBound.IsValid();
  }
}

export class Features {
  constructor() {
    this._referenceEdge = 0;
    this._incidentEdge = 0;
    this._flip = 0;
    this._incidentVertex = 0;
    this._m_id = null;
  }

  get referenceEdge() {
    return this._referenceEdge;
  }

  set referenceEdge(value) {
    this._referenceEdge = value;
    if (this._m_id) {
      this._m_id._key = (this._m_id._key & 0xFFFFFF00) | (this._referenceEdge & 0xFF);
    }
  }

  get incidentEdge() {
    return this._incidentEdge;
  }

  set incidentEdge(value) {
    this._incidentEdge = value;
    if (this._m_id) {
      this._m_id._key = (this._m_id._key & 0xFFFF00FF) | ((this._incidentEdge << 8) & 0xFF00);
    }
  }

  get incidentVertex() {
    return this._incidentVertex;
  }

  set incidentVertex(value) {
    this._incidentVertex = value;
    if (this._m_id) {
      this._m_id._key = (this._m_id._key & 0xFF00FFFF) | ((this._incidentVertex << 16) & 0xFF0000);
    }
  }

  get flip() {
    return this._flip;
  }

  set flip(value) {
    this._flip = value;
    if (this._m_id) {
      this._m_id._key = (this._m_id._key & 0xFFFFFF) | ((this._flip << 24) & 0xFF000000);
    }
  }
}

export class b2ContactID {
  constructor() {
    this._key = 0;
    this.features = new Features();
    this.features._m_id = this;
  }

  Set(contactID) {
    this.key = contactID._key;
  }

  Copy() {
    const id = new b2ContactID();
    id.key = this.key;
    return id;
  }

  set key(value) {
    this._key = value >>> 0;
    this.features._referenceEdge = this._key & 0xFF;
    this.features._incidentEdge = (this._key >> 8) & 0xFF;
    this.features._incidentVertex = (this._key >> 16) & 0xFF;
    this.features._flip = (this._key >> 24) & 0xFF;
  }

  get key() {
    return this._key;
  }
}

export class ClipVertex {
  constructor() {
    this.id = new b2ContactID();
    this.v = new b2Vec2();
  }
}

export class b2ContactPoint {
  constructor() {
    this.friction = null;
    this.separation = null;
    this.normal = new b2Vec2();
    this.position = new b2Vec2();
    this.restitution = null;
    this.shape1 = null;
    this.shape2 = null;
    this.id = new b2ContactID();
    this.velocity = new b2Vec2();
  }
}

export class b2ManifoldPoint {
  constructor() {
    this.separation = 0;
    this.localPoint2 = new b2Vec2();
    this.normalImpulse = 0;
    this.tangentImpulse = 0;
    this.localPoint1 = new b2Vec2();
    this.id = new b2ContactID();
  }

  Set(point) {
    this.localPoint1.SetV(point.localPoint1);
    this.localPoint2.SetV(point.localPoint2);
    this.separation = point.separation;
    this.normalImpulse = point.normalImpulse;
    this.tangentImpulse = point.tangentImpulse;
    this.id.key = point.id.key;
  }

  Reset() {
    this.localPoint1.SetZero();
    this.localPoint2.SetZero();
    this.separation = 0;
    this.normalImpulse = 0;
    this.tangentImpulse = 0;
    this.id.key = 0;
  }
}

export class b2Manifold {
  static e_circles = 1;
  static e_faceA = 2;
  static e_faceB = 4;

  constructor() {
    this.m_pointCount = 0;
    this.m_type = 0;
    this.m_localPlaneNormal = new b2Vec2();
    this.m_localPoint = new b2Vec2();
    this.m_normal = this.m_localPlaneNormal;
    this.pointCount = 0;
    this.normal = new b2Vec2();
    this.points = new Array(b2Settings.b2_maxManifoldPoints);
    this.m_points = this.points;
    for (let i = 0; i < b2Settings.b2_maxManifoldPoints; i += 1) {
      this.points[i] = new b2ManifoldPoint();
    }
  }

  Set(manifold) {
    this.pointCount = manifold.pointCount;
    this.m_pointCount = manifold.m_pointCount ?? manifold.pointCount;
    this.m_type = manifold.m_type ?? manifold.type ?? 0;
    for (let i = 0; i < b2Settings.b2_maxManifoldPoints; i += 1) {
      this.points[i].Set(manifold.points[i]);
    }
    this.normal.SetV(manifold.normal);
    this.m_localPlaneNormal.SetV(manifold.m_localPlaneNormal ?? manifold.normal);
    this.m_localPoint.SetV(manifold.m_localPoint ?? { x: 0, y: 0 });
  }

  Reset() {
    for (let i = 0; i < b2Settings.b2_maxManifoldPoints; i += 1) {
      this.points[i].Reset();
    }
    this.normal.SetZero();
    this.m_localPlaneNormal.SetZero();
    this.m_localPoint.SetZero();
    this.pointCount = 0;
    this.m_pointCount = 0;
    this.m_type = 0;
  }
}

export class b2OBB {
  constructor() {
    this.R = new b2Mat22();
    this.center = new b2Vec2();
    this.extents = new b2Vec2();
  }
}

export class b2Point {
  constructor() {
    this.p = new b2Vec2();
  }

  GetFirstVertex() {
    return this.p;
  }

  Support() {
    return this.p;
  }
}

export class b2Segment {
  constructor() {
    this.p1 = new b2Vec2();
    this.p2 = new b2Vec2();
  }

  TestSegment(lambda, normal, segment, maxLambda) {
    const s = segment.p1;
    const rX = segment.p2.x - s.x;
    const rY = segment.p2.y - s.y;
    const dX = this.p2.x - this.p1.x;
    const dY = this.p2.y - this.p1.y;
    let nX = dY;
    let nY = -dX;
    const kSlop = 100 * Number.MIN_VALUE;
    const denom = -(rX * nX + rY * nY);
    if (denom > kSlop) {
      const bX = s.x - this.p1.x;
      const bY = s.y - this.p1.y;
      let a = bX * nX + bY * nY;
      if (0 <= a && a <= maxLambda * denom) {
        const mu2 = -rX * bY + rY * bX;
        if (-kSlop * denom <= mu2 && mu2 <= denom * (1 + kSlop)) {
          a /= denom;
          const length = Math.sqrt(nX * nX + nY * nY);
          nX /= length;
          nY /= length;
          lambda[0] = a;
          normal.Set(nX, nY);
          return true;
        }
      }
    }
    return false;
  }
}
